import { action, computed, makeAutoObservable } from 'mobx';

import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    ApiGetSportsResponseType,
    apiGetSportsRequest,
    ApiGetSportsResponse200Type,
} from 'src/api_openapi/v2/trading-search/apiGetSports';

import { Resource } from 'src_common/common/mobx-utils/Resource';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { Common } from 'src/AppState/Common';
import {
    BaseTreeType,
    changeIdToName,
    deserializeNodeValue,
    FIRST_PAGE,
    getDisplayLabel,
    InitValuesType,
    isEventTreeType,
    isTreeType,
    PER_PAGE,
    serializeNodeValue,
    sortSportsAlphabetically,
    SportsPickerTreeType,
} from './utils';
import { CompetitionStoreState } from './CompetitionStore.state';
import { SportDetailsLoader } from './SportDetailsLoader';
import { FormHelper, FormHelperFormType } from 'src/domains/casino/webview/bonus-engine/campaign/FormHelper';

export class SportsPickerState {
    private isInit: boolean = true;
    private readOnlyMode: boolean = false;
    public showAllBadges: boolean = false;

    public checked: string[] = [];
    public expanded: string[] = [];
    public loadedNodes: Set<string> = new Set();

    public keywordInputState: FormInputState<string, unknown> = FormInputState.new('');
    public readonly allSportsResource: Resource<ApiGetSportsResponseType | null>;
    public readonly competitionStore: AutoMap<string, CompetitionStoreState>;

    private readonly sportDetailsLoader: SportDetailsLoader;

    private form: FormHelperFormType;

    public constructor(private readonly common: Common) {
        makeAutoObservable(this);

        this.form = FormHelper.form;

        this.allSportsResource = new Resource(async () => {
            return await this.getAllSports();
        });

        this.competitionStore = new AutoMap((sportId: string) => {
            return new CompetitionStoreState(common, sportId, () => this.loadedNodes, this.readOnlyMode);
        });

        this.sportDetailsLoader = new SportDetailsLoader(this.common);
    }

    @action public setInitValues = async (initValues: InitValuesType): Promise<void> => {
        // isInit prevents this function from being called again when the session object is refreshed.
        // Consider how to do it better.
        if (this.isInit === false && this.isTouched() === false) {
            return;
        }

        this.isInit = false;
        const parentNodesToExpand: Set<string> = new Set();

        // Sports
        const checkedSports = initValues.sportIds.map((sportId) => serializeNodeValue({ type: 'sport', sportId }));

        // Competitions
        const checkedCompetitions: Set<string> = new Set();
        for (const competitionId of initValues.competitionIds) {
            const fetchedCompetition = await this.sportDetailsLoader.fetchCompetition(competitionId);

            if (fetchedCompetition === null || fetchedCompetition.type !== 'competition') {
                continue;
            }

            checkedCompetitions.add(serializeNodeValue(fetchedCompetition));
            parentNodesToExpand.add(serializeNodeValue({ type: 'sport', sportId: fetchedCompetition.sportId }));
        }

        // Events
        const checkedEvents: Set<string> = new Set();
        for (const eventId of initValues.eventIds) {
            const fetchedEvent = await this.sportDetailsLoader.fetchEvent(eventId);

            if (fetchedEvent === null || fetchedEvent.type !== 'event') {
                continue;
            }

            checkedEvents.add(serializeNodeValue(fetchedEvent));

            parentNodesToExpand.add(serializeNodeValue({ type: 'sport', sportId: fetchedEvent.sportId }));
            parentNodesToExpand.add(
                serializeNodeValue({
                    type: 'competition',
                    sportId: fetchedEvent.sportId,
                    competitionId: fetchedEvent.competitionId,
                })
            );
        }

        // Markets
        const checkedMarkets: Set<string> = new Set();
        for (const marketId of initValues.marketIds) {
            const fetchedMarket = await this.sportDetailsLoader.fetchMarket(marketId);

            if (fetchedMarket === null || fetchedMarket.type !== 'market') {
                continue;
            }

            checkedMarkets.add(serializeNodeValue(fetchedMarket));

            parentNodesToExpand.add(serializeNodeValue({ type: 'sport', sportId: fetchedMarket.sportId }));
            parentNodesToExpand.add(
                serializeNodeValue({
                    type: 'competition',
                    sportId: fetchedMarket.sportId,
                    competitionId: fetchedMarket.competitionId,
                })
            );
            parentNodesToExpand.add(
                serializeNodeValue({
                    type: 'event',
                    sportId: fetchedMarket.sportId,
                    competitionId: fetchedMarket.competitionId,
                    eventId: fetchedMarket.eventId,
                })
            );
        }

        this.setChecked([...checkedSports, ...checkedCompetitions, ...checkedEvents, ...checkedMarkets]);
        this.handleExpand(Array.from(parentNodesToExpand));
    };

    @computed public get filteredNodes(): SportsPickerTreeType[] {
        const keyword = this.keyword.toLowerCase();
        if (keyword.trim() === '') return this.allSportsForView;

        return this.filterNodes(this.allSportsForView, keyword);
    }

    public async getAllSports(): Promise<ApiGetSportsResponseType | null> {
        const getSportsReponse = await apiGetSportsRequest(
            `${this.common.appEnvParams.gatewayUrl}`,
            {
                page: FIRST_PAGE,
                pageSize: PER_PAGE,
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );

        return getSportsReponse;
    }

    public get allSports(): ApiGetSportsResponse200Type | 'loading' | 'error' {
        const res = this.allSportsResource.get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error' || res.value === null || res.value.status !== 200) {
            return 'error';
        }

        return sortSportsAlphabetically(changeIdToName(res.value.body));
    }

    @computed public get getLoadedSportIds(): string[] {
        const loadedSportIds: Array<string> = [];
        for (const nodeValue of this.loadedNodes) {
            const loadedDeserialized = deserializeNodeValue(nodeValue);
            if (loadedDeserialized !== null && loadedDeserialized.type === 'sport') {
                loadedSportIds.push(loadedDeserialized.sportId);
            }
        }
        return loadedSportIds;
    }

    @computed public get isLoading(): boolean {
        if (this.allSports === 'loading') {
            return true;
        }

        let isLoading = false;
        this.getLoadedSportIds.map((sportId) => {
            if (this.competitionStore.get(sportId).isLoading === true) {
                isLoading = true;
            }
        });
        return isLoading;
    }

    @computed public get allSportsForView(): SportsPickerTreeType[] {
        if (this.allSports === 'loading') {
            return [];
        }

        if (this.allSports === 'error') {
            // Toast Error ?
            return [];
        }

        return this.allSports.items.map((sport) => {
            let competitions: Array<BaseTreeType> = [];

            if (this.getLoadedSportIds.includes(sport.sportId)) {
                const data = this.competitionStore.get(sport.sportId).getCompetitions;

                if (data === 'error') {
                    // Toast Error ?
                }

                if (data !== 'loading' && data !== 'error') {
                    competitions = data;
                }
            }

            const serializedValue = serializeNodeValue({ type: 'sport', sportId: sport.sportId });
            return {
                id: serializedValue,
                value: serializedValue,
                label: sport.name,
                showCheckbox: !this.readOnlyMode,
                children: competitions,
            };
        });
    }

    private filterNodes = (nodes: SportsPickerTreeType[], keyword: string): SportsPickerTreeType[] => {
        const filteredNodes: SportsPickerTreeType[] = [];

        for (const node of nodes) {
            if (getDisplayLabel(node).toLowerCase().includes(keyword)) {
                filteredNodes.push(node);
                continue;
            }

            if (typeof node.children !== 'undefined') {
                const filteredChildren = this.filterNodes(node.children, keyword);

                if (filteredChildren.length > 0) {
                    if (isTreeType(node) || isEventTreeType(node)) {
                        filteredNodes.push({
                            ...node,
                            children: filteredChildren.filter(isTreeType),
                        });
                    } else {
                        filteredNodes.push({
                            ...node,
                            children: filteredChildren,
                        });
                    }
                }
            }
        }

        return filteredNodes;
    };

    public handleCheck = (checked: string[]): void => {
        this.setChecked(checked);
        this.setIsTouched();
    };

    @action public handleExpand = (expanded: string[]): void => {
        this.expanded = expanded;
        expanded.map((value) => this.loadedNodes.add(value));
    };

    @action public setChecked = (checked: string[]): void => {
        this.checked = checked;
    };

    private setIsTouched(): void {
        if (this.isTouched() === false) {
            FormHelper.setIsTouched(this.form);
        }
    }

    public isTouched(): boolean {
        return this.form.isTouched();
    }

    public resetIsTouched(): void {
        if (this.isTouched()) {
            FormHelper.resetIsTouched(this.form);
        }
    }

    public expandToKeyword = (keyword: string): void => {
        const keywordLower = keyword.toLowerCase();
        const expandedKeys: string[] = [];

        if (keyword.length < 4) {
            return;
        }

        const expandMatchingNodes = (nodes: SportsPickerTreeType[], parentKeys: string[] = []): void => {
            for (const node of nodes) {
                const currentKeys = [...parentKeys, node.value];
                if (getDisplayLabel(node).toLowerCase().includes(keywordLower)) {
                    expandedKeys.push(...currentKeys);
                }
                if (node.children !== undefined) {
                    expandMatchingNodes(node.children, currentKeys);
                }
            }
        };

        expandMatchingNodes(this.allSportsForView);
        this.handleExpand([...new Set(expandedKeys)]);
    };

    public setKeyword = (keyword: string): void => {
        this.keywordInputState.setValue(keyword);
        this.expandToKeyword(keyword);
    };

    public clearChecked = (): void => {
        if (this.checked.length > 0 && this.isTouched() === false) {
            this.setIsTouched();
        }

        this.checked = [];
        this.showAllBadges = false;
    };

    public get keyword(): string {
        return this.keywordInputState.value;
    }

    public get visibleChecked(): { display: string[]; remaining: number } {
        const maxVisible = 3;

        if (this.showAllBadges) {
            return { display: this.checked, remaining: 0 };
        }

        const visibleChecked = this.checked.slice(0, maxVisible);
        const remaining = Math.max(0, this.checked.length - maxVisible);
        return { display: visibleChecked, remaining };
    }

    public handleRemoveChecked = (value: string): void => {
        this.setIsTouched();
        this.setChecked(this.checked.filter((item) => item !== value));
    };

    public toggleShowAllBadges = (): void => {
        this.showAllBadges = !this.showAllBadges;
    };

    public get visibleCheckedWithLabels(): { value: string; label: string }[] {
        const maxVisible = 3;
        const checkedToDisplay = this.showAllBadges ? this.checked : this.checked.slice(0, maxVisible);

        const getLabelForItem = (serializedValue: string): string => {
            const deserializedValue = deserializeNodeValue(serializedValue);
            const item = this.allSportsForView
                .flatMap((sports) => {
                    if (deserializedValue === null) {
                        return [];
                    }
                    if (deserializedValue.type === 'sport') {
                        return sports;
                    }
                    if (sports.children === undefined) return [];

                    return sports.children.flatMap((competitions) => {
                        if (deserializedValue.type === 'competition') {
                            return competitions;
                        }
                        if (competitions.children === undefined) return [];

                        return competitions.children.flatMap((events) => {
                            if (deserializedValue.type === 'event') {
                                return events;
                            }

                            if (events.children === undefined) return [];

                            return events.children.flatMap((markets) => {
                                return markets;
                            });
                        });
                    });
                })
                .find((item) => item.value === serializedValue);

            if (item !== undefined) {
                return isEventTreeType(item) ? item.name : item.label;
            }

            return '';
        };

        return checkedToDisplay.map((value) => {
            const desetializedValue = deserializeNodeValue(value);
            return {
                value,
                label: `${desetializedValue?.type}: ${getLabelForItem(value)}`,
            };
        });
    }

    public setReadOnlyMode = (value: boolean): void => {
        this.readOnlyMode = value;
    };
}
